import {Injectable, Optional} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {DeviceCart} from '@kion/data-models';
import {HttpClient} from '@angular/common/http';
import {ApiConfig} from '../../../../../api-endpoint/src/lib/api.config';

interface IResponseItem {
  type: string,
  code: string,
  employee: {
    uid: string
  },
  entries: any,
  guid: string,
  totalItems: number,
  totalPrice: Price,
  totalPriceWithTax: Price,
  purchaseOrderNumber: string,
  purchaseOrderCreationDateString: string,
  userComment: Note,
  additionalCcEmails: string[],
  status: string
}

export interface Note {
  text: string
}

interface Price {
  currencyIso: string
  formattedValue: string
  priceType: string
  value: number
}

@Injectable({
  providedIn: 'root'
})
export class DeviceCartConnectors {

  private readonly _rootUrl: string;
  private readonly _baseSite: string;
  private route = '';

  constructor(
    @Optional() apiConfig: ApiConfig,
    private http: HttpClient,
  ) {
    this._rootUrl = apiConfig.rootUrl;
    this._baseSite = apiConfig.baseSite;
  }

  private static transformFromApi(response: IResponseItem): DeviceCart {
    return {
      ...response,
      id: response.guid,
      deviceId: ''
    };
  }

  getCartForDevice(factory: string, deviceId: string): Observable<DeviceCart> {
    return this.http.get<IResponseItem>(this.getDeviceCartUrl(factory, deviceId)).pipe(
      map(
        (response: IResponseItem) => DeviceCartConnectors.transformFromApi(response)
      ),
      catchError(error => throwError(error))
    )
  }

  saveCart(factory: string, deviceId: string, id: string): Observable<DeviceCart> {
    return this.http.post<void>(this.getDeviceCartUrl(factory, deviceId) + '/' + id, {}).pipe(
      switchMap(
        () => this.http.get<IResponseItem>(this.getDeviceCartUrl(factory, deviceId)).pipe(
          map(
            (res: IResponseItem) => DeviceCartConnectors.transformFromApi(res)
          ),
          catchError(error => throwError(error))
        )
      ),
      catchError(error => throwError(error))
    )
  }

  addToCart(factory: string, deviceId: string, id: string, featureId: string, salesRule: string): Observable<DeviceCart> {
    const request = {
      product: {
        code: featureId
      },
      rulePatternId: salesRule
    };
    return this.http.post<void>(this.getAddToCartUrl(id), request).pipe(
      switchMap(
        () => this.saveCart(factory, deviceId, id)
      ),
      catchError(error => throwError(error))
    )
  }

  placeOrder(cartId: string): Observable<string> {
    return this.http.post<any>(this.getPlaceOrderUrl() + '?cartId=' + cartId, {}).pipe(
      map(
        (response) => response.code
      ),
      catchError(error => throwError(error))
    );
  }

  removeFromCart(factory: string, deviceId: string, cartId: string, entry: number): Observable<DeviceCart> {
    return this.http.delete<void>(this.getAddToCartUrl(cartId) + '/' + entry).pipe(
      switchMap(
        () => this.saveCart(factory, deviceId, cartId)
      ),
      catchError(error => throwError(error))
    )
  }

  update(
    factory: string,
    deviceId: string,
    quoteId: string,
    state: string,
    note: string,
    poNumber: string,
    ccRecipients: string[],
    poDate: string,
    cartId: string
  ): Observable<DeviceCart> {
    const request = {
      userComment: {
        text: note
      },
      purchaseOrderNumber: poNumber,
      additionalCcEmails : ccRecipients,
      status: state
    };
    return this.http.post<void>(this.getUpdateCartUrl(factory, deviceId, cartId), request).pipe(
      switchMap(
        () => this.saveCart(factory, deviceId, cartId)
      ),
      catchError(
        error => throwError(error)
      )
    )
  }

  delete(factory: string, deviceId: string): Observable<void> {
    return this.http.delete(this.getDeleteQuoteUrl(factory, deviceId)).pipe(
      map(
        () => null
      ),
      catchError(error => throwError(error))
    );
  }

  protected getManyUrl() {
    return this._rootUrl + this.route;
  }

  protected getOneUrl(id: number) {
    return this._rootUrl + this.route + id;
  }

  protected getDeviceCartUrl(factory: string, deviceId: string): string {
    return this._rootUrl + this._baseSite + '/devices/manufacturer/' + factory + '/serialnumber/' + deviceId + '/cart'
  }

  protected getAddToCartUrl(cartId: string): string {
    return this._rootUrl + this._baseSite + '/users/anonymous/carts/' + cartId + '/entries'
  }

  protected getPlaceOrderUrl(): string {
    return this._rootUrl + this._baseSite + '/users/anonymous/orders'
  }

  protected getUpdateCartUrl(factory: string, deviceId: string, cartId: string): string {
    return this._rootUrl + this._baseSite + '/devices/manufacturer/' + factory + '/serialnumber/' + deviceId + '/cart/' + cartId + '/update'
  }

  protected getDeleteQuoteUrl(factory: string, deviceId: string): string {
    return this._rootUrl + this._baseSite + '/devices/manufacturer/' + factory + '/serialnumber/' + deviceId + '/quote'
  }
}
